import React from 'react';

import { ResultsNone } from '../../../../common/ui/results-none';
import { ResultsNoneContentCleared } from '../../../../common/ui/results-none/results-none-content-cleared';
import ResultsPaginationFooter from '../../../../common/ui/results-pagination-footer';
import { SearchResultsList } from '../../../../common/ui/search-result-list';
import type { ResultDerivedDataProps } from '../result-derived-data';
import {
	CandidateInterleavedSearchResultContent,
	SearchResult,
	SearchResultRecord,
	shouldRenderInterleavedSearchResultContent,
} from '../search-result';
import type { SearchResultsListRendererProps } from '../types';

export const SearchResultsListDefaultRenderer = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	isCommentsFilterActive,
	productConfigs,
	containsSearchResults,
	edgesToDisplay,
	cloudId,
	appMode,
	searchQuery,
	thirdPartyConfigs,
	selectedSites,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	searchSessionId,
	originalQuery,
	availableProducts,
	logError,
	teamNames,
	issueStatuses,
	linkedPRs,
	pastMeetingOccurrences,
	smartAnswersPortalRef,
	mainContentWidth,
	wasContentClearedBySpaceChange,
	clearSpaceFilter,
	clearFilters,
	getSelectedFilters,
	cursor,
	isLoadingNext,
	fetchNextPage,
	renderConnectorPromo,
	isResultRowSubscriptionHoistingEnabled,
}: SearchResultsListRendererProps & ResultDerivedDataProps): React.JSX.Element => {
	return (
		<SearchResultsList containsSearchResults={containsSearchResults}>
			{containsSearchResults ? (
				edgesToDisplay.map(
					(edge, position) =>
						edge?.node &&
						(isResultRowSubscriptionHoistingEnabled ? (
							<React.Fragment key={edge.node.id}>
								{shouldRenderInterleavedSearchResultContent(position, edgesToDisplay.length) && (
									<CandidateInterleavedSearchResultContent
										position={position}
										totalResults={edgesToDisplay.length}
										cloudId={cloudId}
										appMode={appMode}
										searchQuery={searchQuery}
										thirdPartyConfigs={thirdPartyConfigs}
										selectedProduct={selectedProduct}
										primaryProduct={primaryProduct}
										primarySubProduct={primarySubProduct}
										searchSessionId={searchSessionId}
										originalQuery={originalQuery}
										availableProducts={availableProducts}
										logError={logError}
										renderConnectorPromo={renderConnectorPromo}
									/>
								)}
								<SearchResultRecord
									preparedResultAttributes={preparedResultAttributes}
									intentDetectionTriggered={intentDetectionTriggered}
									isCommentsFilterActive={isCommentsFilterActive}
									productConfigs={productConfigs}
									node={edge.node}
									position={position}
									totalResults={edgesToDisplay.length}
									cloudId={cloudId}
									appMode={appMode}
									searchQuery={searchQuery}
									thirdPartyConfigs={thirdPartyConfigs}
									selectedSites={selectedSites}
									selectedProduct={selectedProduct}
									primaryProduct={primaryProduct}
									primarySubProduct={primarySubProduct}
									searchSessionId={searchSessionId}
									originalQuery={originalQuery}
									availableProducts={availableProducts}
									logError={logError}
									compassTeamName={teamNames[edge.node.id]}
									jiraIssueStatus={issueStatuses[edge.node.id]}
									jiraLinkedPRs={linkedPRs[edge.node.id]}
									pastMeetingOccurrences={pastMeetingOccurrences?.[edge.node.id]}
									smartAnswersPortalRef={smartAnswersPortalRef}
									mainContentWidth={mainContentWidth}
									renderConnectorPromo={renderConnectorPromo}
								/>
							</React.Fragment>
						) : (
							<SearchResult
								preparedResultAttributes={preparedResultAttributes}
								intentDetectionTriggered={intentDetectionTriggered}
								isCommentsFilterActive={isCommentsFilterActive}
								productConfigs={productConfigs}
								key={edge.node.id}
								node={edge.node}
								position={position}
								totalResults={edgesToDisplay.length}
								cloudId={cloudId}
								appMode={appMode}
								searchQuery={searchQuery}
								thirdPartyConfigs={thirdPartyConfigs}
								selectedSites={selectedSites}
								selectedProduct={selectedProduct}
								primaryProduct={primaryProduct}
								primarySubProduct={primarySubProduct}
								searchSessionId={searchSessionId}
								originalQuery={originalQuery}
								availableProducts={availableProducts}
								logError={logError}
								compassTeamName={teamNames[edge.node.id]}
								jiraIssueStatus={issueStatuses[edge.node.id]}
								jiraLinkedPRs={linkedPRs[edge.node.id]}
								pastMeetingOccurrences={pastMeetingOccurrences?.[edge.node.id]}
								smartAnswersPortalRef={smartAnswersPortalRef}
								mainContentWidth={mainContentWidth}
								renderConnectorPromo={renderConnectorPromo}
							/>
						)),
				)
			) : wasContentClearedBySpaceChange ? (
				<ResultsNoneContentCleared
					selectedProduct={selectedProduct}
					clearSpaceFilter={clearSpaceFilter}
					clearFilters={clearFilters}
				/>
			) : (
				<ResultsNone
					filtersEnabled={!!selectedProduct || getSelectedFilters().hasFiltersSelected}
					clearFilters={clearFilters}
				/>
			)}
			<ResultsPaginationFooter
				cursor={cursor}
				isLoading={isLoadingNext}
				fetchNextPage={fetchNextPage}
			/>
		</SearchResultsList>
	);
};
